import React, { Fragment } from 'react';
import classNames from 'classnames';

import invoke from 'lodash/invoke';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import toNumber from 'lodash/toNumber';

import { isVideoish } from 'zoomforth_codesbane_viewer/lib/page_content_utils.js';

import { makeVideoishPlayerPropsForContent } from 'zoomforth_tools/videoish_player.js';
import VideoishPlayer from 'zoomforth_component_lib/videoish_player.jsx';

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowCircleLeftIcon,
  ArrowCircleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'zoomforth_component_lib/app_modern_icons.jsx';
import { SLIDESHOW_CONST } from './settings.js';

import CircularSpinner from 'zoomforth_component_lib/circular_spinner.jsx';

const captionGenerator = (contentItem, slideContainerHeight, isMobile) => {
  const { title, caption, slideshowData } = contentItem;

  const {
    slideTextPosition,
    slideSpotlight,
    slideCaptionPosition = SLIDESHOW_CONST.SLIDESHOW_CAPTION_TEXT_ALIGNMENT.LEFT,
    slideTextAlignment,
    slideshowNav,
    slideCaptionOpacity,
  } = slideshowData || {};

  const isSlideNavigationShown = slideshowNav !== SLIDESHOW_CONST.SLIDESHOW_ARROW_TYPE.HIDE;
  const parsedCaptionPosition =
    !isNil(isMobile) && isMobile
      ? SLIDESHOW_CONST.SLIDESHOW_CAPTION_POSITION.BOTTOM
      : slideCaptionPosition;

  return (
    <div
      style={{
        height:
          parsedCaptionPosition ===
          SLIDESHOW_CONST.SLIDESHOW_CAPTION_TEXT_POSITION.BOTTOM
            ? undefined
            : slideContainerHeight,
        justifyContent:
          slideTextPosition ===
          SLIDESHOW_CONST.SLIDESHOW_CAPTION_TEXT_POSITION.BOTTOM
            ? SLIDESHOW_CONST.CSS_CONSTS.JUSTIFY_CONTENT.END
            : slideTextPosition ===
              SLIDESHOW_CONST.SLIDESHOW_CAPTION_TEXT_POSITION.MIDDLE
            ? SLIDESHOW_CONST.CSS_CONSTS.JUSTIFY_CONTENT.CENTER
            : SLIDESHOW_CONST.CSS_CONSTS.JUSTIFY_CONTENT.START,
        opacity: toNumber(slideCaptionOpacity) / 100,
      }}
      className={classNames('slide-caption', {
        'slide-spotlight-center':
          slideSpotlight === SLIDESHOW_CONST.SLIDESHOW_SPOTLIGHT_TYPE.CENTER,
        'left-placement':
          parsedCaptionPosition ===
          SLIDESHOW_CONST.SLIDESHOW_CAPTION_POSITION.LEFT,
        'bottom-placement':
          parsedCaptionPosition ===
          SLIDESHOW_CONST.SLIDESHOW_CAPTION_POSITION.BOTTOM,
        'right-placement':
          parsedCaptionPosition ===
          SLIDESHOW_CONST.SLIDESHOW_CAPTION_POSITION.RIGHT,
        'no-navigation': !isSlideNavigationShown,
      })}
    >
      <div
        style={{
          textAlign:
            slideTextAlignment ===
            SLIDESHOW_CONST.SLIDESHOW_CAPTION_TEXT_ALIGNMENT.RIGHT
              ? SLIDESHOW_CONST.CSS_CONSTS.JUSTIFY_CONTENT.END
              : slideTextAlignment ===
                SLIDESHOW_CONST.SLIDESHOW_CAPTION_TEXT_ALIGNMENT.CENTER
              ? SLIDESHOW_CONST.CSS_CONSTS.JUSTIFY_CONTENT.CENTER
              : SLIDESHOW_CONST.CSS_CONSTS.JUSTIFY_CONTENT.START,
        }}
        className={classNames('caption-contents-wrapper')}
      >
        {
          title &&
            <div className="carousel-caption-title">{title}</div>
        }
        {
          caption &&
            <div
              className="carousel-caption-content"
              dangerouslySetInnerHTML={{ __html: caption }}
            />
        }
      </div>
    </div>
  );
};

export const getSlideNavigationArrows = (
  slideArrowType = SLIDESHOW_CONST.SLIDESHOW_ARROW_SHAPE.ARROW,
  direction
) => {
  if (slideArrowType === SLIDESHOW_CONST.SLIDESHOW_ARROW_SHAPE.ARROW)
    return direction === SLIDESHOW_CONST.DIRECTION.LEFT ? (
      <ArrowLeftIcon className="fa-2x" aria-hidden="true" />
    ) : (
      <ArrowRightIcon className="fa-2x" aria-hidden="true" />
    );

  if (slideArrowType === SLIDESHOW_CONST.SLIDESHOW_ARROW_SHAPE.CHEVRON)
    return direction === SLIDESHOW_CONST.DIRECTION.LEFT ? (
      <ChevronLeftIcon className="fa-2x" aria-hidden="true" />
    ) : (
      <ChevronRightIcon className="fa-2x" aria-hidden="true" />
    );
  if (slideArrowType === SLIDESHOW_CONST.SLIDESHOW_ARROW_SHAPE.CIRCULAR)
    return direction === SLIDESHOW_CONST.DIRECTION.LEFT ? (
      <ArrowCircleLeftIcon className="fa-4x" aria-hidden="true" />
    ) : (
      <ArrowCircleRightIcon className="fa-4x" aria-hidden="true" />
    );
};

export const slideGenerator = (
  contentItemAssetPairs,
  slideHeight,
  onOverlayOpen,
  slideshowConfig,
  isMobile,
  currentItem
) => {
  const { slideFullScreen, slideSpotlight, slideshowNav } = slideshowConfig;

  return contentItemAssetPairs.map(({ contentItem, asset }, index) => {
    const { slideshowData } = contentItem;

    if (asset) {
      const { uuid, title, alt_text, caption } = contentItem;

      const {
        slideHeightUnit,
        slideHeightValue,
        slideWidthUnit,
        slideWidthValue,
      } = slideshowData;

      const { displayImage } = asset;
      const isPlayable = isVideoish(contentItem);

      const {
        slideCaptionPosition = SLIDESHOW_CONST.SLIDESHOW_CAPTION_POSITION.LEFT,
        slideCaptionDisplay = true,
      } = slideshowData;

      const slideHasCaption = slideCaptionDisplay && (title || caption);

      const isSlideNavigationShown =
        slideshowNav !== SLIDESHOW_CONST.SLIDESHOW_ARROW_TYPE.HIDE;

      const parseSlideHeight = (slideHeight) =>
        isString(slideHeight) ? Number(slideHeight.match(/\d*/)) : slideHeight;

      let slideContainerHeight;
      if (
        slideCaptionPosition !==
        SLIDESHOW_CONST.SLIDESHOW_CAPTION_POSITION.BOTTOM
      ) {
        slideContainerHeight = isSlideNavigationShown
          ? parseSlideHeight(slideHeight)
          : parseSlideHeight(slideHeight);
      }

      /**
       * This function is responsible for calculating the inline styling that is to be applied to each slide. 
       * Each slide is wrapped by a container. We need to calculate the inline styling to accommodate caption display 
       * and its position. We want to ensure that the slide content is not overlapped by the caption or the navigation bar
       * at the bottom of the slide. The initial mocks called for a caption overlap, but this raised issues, such as 
       * caption content readability when contrasted against the backdrop image. We may include this capability in a v2
       * but we would have to add additional controls to the caption, in regards to background color and font contrast.
       * @returns an object with styling that is spread on the container div.
       * 
       * I'm not particularly happy with this approach, but it does the work. A better approach would be to have a container 
       * that automatically fits to the provided container. However even with this approach we'd have to likely wire in inline styling
       * to have the image contain/cover
       */
      const imageHeightCalculator = () => {
        const parsedHeightValue = parseSlideHeight(slideHeight);
        let calculatedHeight;
        if (slideCaptionPosition === SLIDESHOW_CONST.SLIDESHOW_CAPTION_POSITION.BOTTOM) {
          // if we have a caption displayed at the bottom we're subtraction 25% of the original size
          // to accomodate the bottom cpation position, while having the image fit above the caption
          // ensuring no overlap. 
          // The caption is displayed if the setting is true, and there is caption content
          if (slideCaptionDisplay && (title || caption)) {
            calculatedHeight = Math.floor( parsedHeightValue - parsedHeightValue * 0.25);
          }
        } else {
          calculatedHeight = parsedHeightValue;
        }

        // if the slide is set to custom we want to apply whatever measurement is specified to the container
        // appending the selected unit
        if (slideshowData.slideType === SLIDESHOW_CONST.SLIDE_TYPE.CUSTOM) {
          calculatedHeight = `${slideHeightValue}${slideHeightUnit}`;
        }

        let topValue;
        // the topValue is used to ensure that the image is pegged to the top of the container if the caption is in bottom position
        if (
          slideCaptionPosition === SLIDESHOW_CONST.SLIDESHOW_CAPTION_POSITION.BOTTOM &&
          slideshowData.slideType !== SLIDESHOW_CONST.SLIDE_TYPE.CUSTOM
        ) {
          topValue = 0;
        } else if (
          slideCaptionPosition === SLIDESHOW_CONST.SLIDESHOW_CAPTION_POSITION.BOTTOM &&
          slideshowData.slideType === SLIDESHOW_CONST.SLIDE_TYPE.CUSTOM
        ) {
          // if caption position is bottom and slide type is custom we calculate the top position, as the
          // image height can vary depending on user input
          topValue = slideHeight - calculatedHeight;
        } else {
          // otherwise ignore
          topValue = null;
        }

        return {
          height: calculatedHeight,
          width:
            // width should be 100% excpet if the user provides a width
            slideshowData.slideType === SLIDESHOW_CONST.SLIDE_TYPE.CUSTOM ? `${slideWidthValue}${slideWidthUnit}` : '100%',
            // objectFit 
          objectFit: isMobile
            ? SLIDESHOW_CONST.CSS_CONSTS.OBJECT_FIT.COVER
            : slideshowData.slideType === SLIDESHOW_CONST.SLIDE_TYPE.CUSTOM
            ? SLIDESHOW_CONST.CSS_CONSTS.OBJECT_FIT.UNSET
            : slideshowData.slideType
            ? slideshowData.slideType
            : SLIDESHOW_CONST.CSS_CONSTS.OBJECT_FIT.CONTAIN,
          // for cases in which caption is bottom and slide is not custom, position should be absolute
          position:
            slideCaptionPosition === SLIDESHOW_CONST.SLIDESHOW_CAPTION_POSITION.BOTTOM &&
            slideshowData.slideType !== SLIDESHOW_CONST.SLIDE_TYPE.CUSTOM
              ? SLIDESHOW_CONST.CSS_CONSTS.ABSOLUTE
              : null,
          top: topValue,
          // when caption is at the bottom and slide is not custom type, we want to have the 
          // caption placed to the left at 0. This is alsto the reason why we're setting
          // position to absolute, based on the same conditions
          left:
            slideCaptionPosition === SLIDESHOW_CONST.SLIDESHOW_CAPTION_POSITION.BOTTOM &&
            slideshowData.slideType !== SLIDESHOW_CONST.SLIDE_TYPE.CUSTOM
              ? 0
              : null,
        };
      };

      const videoEventProps = { autoplay: false };

      var videoishPlayerProps = {};
      var uniqueKey = isPlayable ? uuid + currentItem : uuid;

      if (isPlayable) {
        videoishPlayerProps = makeVideoishPlayerPropsForContent(contentItem, asset);
        const { posterImageUrl } = videoishPlayerProps;

        // Generate a unique key based on the posterImageUrl. The idea here is that
        // changing the poster image should force the video player to update. The
        // problem is the VideoishPlayer doesn't support updates due to the way we
        // instantiate VideoJS. This means it's the parent's responsibility to
        // mount / unmount the component and the best means of doing that is
        // through React keys. A changed key means the whole thing remounts.
        uniqueKey = `${isPlayable ? uuid + currentItem : uuid}-${posterImageUrl}`;
      }

      var thisVideoPlayer = null;

      return (
        <div
          id={`slide-${index + 1}`}
          key={uniqueKey}
          className={classNames('slide-wrapper-outline', {
            'slide-wrapper-outline-left':
              slideCaptionPosition ===
              SLIDESHOW_CONST.SLIDESHOW_CAPTION_POSITION.LEFT,

            'slide-wrapper-outline-right':
              slideCaptionPosition ===
              SLIDESHOW_CONST.SLIDESHOW_CAPTION_POSITION.RIGHT,

            isMobileView: isMobile,
            'is-full-screen-clicked-enabled': slideFullScreen
          })}
          style={{
            margin:
              slideSpotlight === SLIDESHOW_CONST.SLIDESHOW_SPOTLIGHT_TYPE.CENTER
                ? '0 10px'
                : null,
          }}
          onClick={() => {
            if (slideFullScreen && onOverlayOpen) {
              // we want to pause the video in te slideshow itself so the
              // overlay does not overlap it.
              if (thisVideoPlayer && thisVideoPlayer._player) {
                thisVideoPlayer._player.pause();
              }

              onOverlayOpen(uuid);
            }
          }}
        >
          <Fragment>
            <div
              className="slide-container"
              style={{
                height: `${slideContainerHeight}`,
                width: '100%',
              }}
            >
              <Fragment>
                {isPlayable ? (
                  <div
                    className="video-player-wrapper"
                    style={{ ...imageHeightCalculator() }}
                  >
                    <VideoishPlayer
                      { ...videoishPlayerProps }
                      autoplay={false}
                      getVideoPlayer={(videoPlayer)=> thisVideoPlayer = videoPlayer}
                      onEnded={(videoEventData) => {
                        invoke(
                          videoEventProps,

                          SLIDESHOW_CONST.VIDEO_EVENTS.ON_ENDED,
                          contentItem,
                          videoEventData
                        );
                      }}
                      onError={(videoEventData) => {
                        invoke(
                          videoEventProps,

                          SLIDESHOW_CONST.VIDEO_EVENTS.ON_ERROR,
                          contentItem,
                          videoEventData
                        );
                      }}
                      onPause={(videoEventData) => {
                        invoke(
                          videoEventProps,

                          SLIDESHOW_CONST.VIDEO_EVENTS.ON_PAUSE,
                          contentItem,
                          videoEventData
                        );
                      }}
                      onPlay={(videoEventData) => {
                        invoke(
                          videoEventProps,

                          SLIDESHOW_CONST.VIDEO_EVENTS.ON_PLAY,
                          contentItem,
                          videoEventData
                        );
                      }}
                      onSeeked={(videoEventData) => {
                        invoke(
                          videoEventProps,

                          SLIDESHOW_CONST.VIDEO_EVENTS.ON_SEEKED,
                          contentItem,
                          videoEventData
                        );
                      }}
                      onStart={(videoEventData) => {
                        invoke(
                          videoEventProps,

                          SLIDESHOW_CONST.VIDEO_EVENTS.ON_START,
                          contentItem,
                          videoEventData
                        );
                      }}
                      onTimeUpdate={(videoEventData) => {
                        invoke(
                          videoEventProps,
                          SLIDESHOW_CONST.VIDEO_EVENTS.ON_TIME_UPDATE,
                          contentItem,
                          videoEventData
                        );
                      }}
                    />
                  </div>
                ) : (
                  <img
                    alt={alt_text}
                    style={{ ...imageHeightCalculator() }}
                    src={displayImage}
                  />
                )}
              </Fragment>
            </div>
            {slideHasCaption &&
              captionGenerator(contentItem, slideContainerHeight, isMobile)}
          </Fragment>
        </div>
      );
    } else {
      const { isPreparing, isProcessing, isEncoding } = contentItem;
      return (
        <div
          className="spinner-wrapper"
          style={{
            height: slideHeight,
          }}
        >
          <div className="slide-spinner-container">
            <CircularSpinner width="100%" height="100%" />
          </div>
          <div className="loading-message">
            {isPreparing
              ? SLIDESHOW_CONST.LOADING_MESSAGES.PREPARING
              : isProcessing
              ? SLIDESHOW_CONST.LOADING_MESSAGES.PROCESSING
              : isEncoding
              ? SLIDESHOW_CONST.LOADING_MESSAGES.ENCODING
              : SLIDESHOW_CONST.LOADING_MESSAGES.LOADING}
          </div>
        </div>
      );
    }
  });
};

export const thumbnailsExtractor = (contentItemAssetPairs) =>
  contentItemAssetPairs.map(({ contentItem, asset }) => {
    // if asset is null send a spinner as a thumbnail url
    return {
      uuid: contentItem.uuid,
      altText: contentItem.alt_text,
      thumbnailUrl: isNil(asset) ? SLIDESHOW_CONST.MISC.PROCESSING : asset.thumbnail,
      isVideo: isVideoish(contentItem),
    };
  });
